import React, { useEffect, useState } from "react";
import { Box } from "../../../design-system";
import {
  DemoVideo, DemoSample, SAMPLES, fetchDemoLibrary, isLoggedIn, selectDemoVideo,
  selectSample, requestUpload, getDemoSelectionId,
} from "./demoSource";

/**
 * Always-on control that drives the live ET demo (Letters B/K/N/O). Everyone — anonymous
 * visitors included — sees the curated sample ETs (the Film Reel + any featured videos), which
 * load from prod so they always work. Logged-in users also get their own library videos. An
 * "Upload a video" option is always offered so a new user with nothing uploaded has a way in.
 * The choice is announced through demoSource (window event); the embeds subscribe and repoint.
 */
const UPLOAD_VALUE = "__upload__";

const DemoVideoPicker: React.FC = () => {
  const [videos, setVideos] = useState<DemoVideo[]>([]);
  const [value, setValue] = useState<string>(() => getDemoSelectionId());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    if (!isLoggedIn()) return;
    fetchDemoLibrary().then((v) => { if (alive) setVideos(v); });
    return () => { alive = false; };
  }, []);

  const handle = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    setErr("");

    if (v === UPLOAD_VALUE) {
      requestUpload();          // page scrolls to / opens its upload box
      return;                   // keep the current selection
    }

    setValue(v);

    // A curated public sample (Film Reel or a featured ET)?
    const sample = SAMPLES.find((s) => s.id === v);
    if (sample) { selectSample(sample); return; }

    // Otherwise it's one of the logged-in user's library videos.
    try {
      setBusy(true);
      await selectDemoVideo(Number(v));
    } catch (e2) {
      setErr("Couldn't load that video — try another.");
      setValue(SAMPLES[0]?.id || "film");
      selectSample(SAMPLES[0]);
    } finally {
      setBusy(false);
    }
  };

  const loggedIn = isLoggedIn();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
      <span style={{ color: "#8d94a8", fontSize: 13, fontWeight: 600, fontFamily: "Outfit, sans-serif" }}>
        Demo video:
      </span>
      <select
        value={value}
        onChange={handle}
        disabled={busy}
        style={{
          background: "#1a1420", color: "#eee", border: "1px solid #3a2b46",
          borderRadius: 8, padding: "6px 10px", fontSize: 13, fontFamily: "Outfit, sans-serif",
          cursor: busy ? "wait" : "pointer", maxWidth: 320,
        }}
      >
        {/* Curated public samples — shown to everyone */}
        {SAMPLES.map((s: DemoSample) => (
          <option key={s.id} value={s.id}>{s.title}</option>
        ))}

        {/* The logged-in user's own library */}
        {loggedIn && videos.length > 0 && (
          <optgroup label="Your videos">
            {videos.map((v) => (
              <option key={v.id} value={String(v.id)}>{v.title}</option>
            ))}
          </optgroup>
        )}

        {/* Always offer a way in for someone with nothing uploaded */}
        <option value={UPLOAD_VALUE}>⬆ Upload a video…</option>
      </select>
      {busy && <span style={{ color: "#8d94a8", fontSize: 12 }}>loading…</span>}
      {err && <span style={{ color: "#ff6b81", fontSize: 12 }}>{err}</span>}
    </Box>
  );
};

export default DemoVideoPicker;
